<template>
    <div>
        <div class="convert-container">
            <form
                novalidate
                @submit.prevent="validateOnSubmit"
            >
                <p>CMYK to Round-Trip RGB</p>

                <response-callout
                    :type="statusType"
                    :messages="status"
                />

                <div
                    class="form-group"
                    :class="{'has-error': errors.has('account')}"
                >
                    <input
                        v-model.trim="account"
                        v-validate="'required'"
                        type="text"
                        name="account"
                        class="form-control"
                        placeholder="Account"
                    >
                </div>
                <div
                    class="form-group"
                    :class="{'has-error': errors.has('icc')}"
                >
                    <input
                        v-model.trim="icc"
                        v-validate="'required'"
                        type="text"
                        name="icc"
                        class="form-control"
                        placeholder="ICC Profile without extension (e.g. GRACoL2013)"
                    >
                </div>

                <div class="row cmyk-row">
                    <div class="col-xs-3">
                        <div
                            class="form-group"
                            :class="{'has-error': errors.has('c')}"
                        >
                            <input
                                v-model.number="c"
                                v-validate="'required|between:0,100'"
                                type="number"
                                name="c"
                                class="form-control"
                                placeholder="C"
                                min="0"
                                max="100"
                            >
                        </div>
                    </div>
                    <div class="col-xs-3">
                        <div
                            class="form-group"
                            :class="{'has-error': errors.has('m')}"
                        >
                            <input
                                v-model.number="m"
                                v-validate="'required|between:0,100'"
                                type="number"
                                name="m"
                                class="form-control"
                                placeholder="M"
                                min="0"
                                max="100"
                            >
                        </div>
                    </div>
                    <div class="col-xs-3">
                        <div
                            class="form-group"
                            :class="{'has-error': errors.has('y')}"
                        >
                            <input
                                v-model.number="y"
                                v-validate="'required|between:0,100'"
                                type="number"
                                name="y"
                                class="form-control"
                                placeholder="Y"
                                min="0"
                                max="100"
                            >
                        </div>
                    </div>
                    <div class="col-xs-3">
                        <div
                            class="form-group"
                            :class="{'has-error': errors.has('k')}"
                        >
                            <input
                                v-model.number="k"
                                v-validate="'required|between:0,100'"
                                type="number"
                                name="k"
                                class="form-control"
                                placeholder="K"
                                min="0"
                                max="100"
                            >
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-xs-12">
                        <div class="form-group">
                            <button
                                type="submit"
                                class="btn btn-primary"
                            >
                                Convert
                            </button>
                        </div>
                    </div>

                    <preloader :working="loading" />
                </div>
            </form>

            <div
                v-if="result"
                class="convert-result"
            >
                <div class="result-swatches">
                    <div
                        class="swatch"
                        :style="{ backgroundColor: result.hex }"
                    />
                </div>

                <table class="table table-condensed">
                    <tbody>
                        <tr>
                            <td>RGB</td>
                            <td>{{ result.rgb.r }}, {{ result.rgb.g }}, {{ result.rgb.b }}</td>
                        </tr>
                        <tr>
                            <td>Hex</td>
                            <td>{{ result.hex }}</td>
                        </tr>
                        <tr>
                            <td>Requested CMYK</td>
                            <td>{{ result.requestedCmyk.c }}, {{ result.requestedCmyk.m }}, {{ result.requestedCmyk.y }}, {{ result.requestedCmyk.k }}</td>
                        </tr>
                        <tr>
                            <td>Verify CMYK</td>
                            <td>{{ result.verifyCmyk.c }}, {{ result.verifyCmyk.m }}, {{ result.verifyCmyk.y }}, {{ result.verifyCmyk.k }}</td>
                        </tr>
                        <tr>
                            <td>Delta</td>
                            <td :class="{ 'text-success': result.isExact, 'text-warning': !result.isExact }">
                                {{ result.delta.c }}, {{ result.delta.m }}, {{ result.delta.y }}, {{ result.delta.k }}
                                <span v-if="result.isExact">(exact)</span>
                                <span v-else>(drift)</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
    import axios from 'axios';

    import Preloader from './Preloader.vue';
    import ResponseCallout from './ResponseCallout.vue';

    import * as api from './api';

    export default {
        components: {
            Preloader,
            ResponseCallout,
        },
        data() {
            return {
                statusType: null,
                status: null,
                account: '',
                icc: '',
                c: null,
                m: null,
                y: null,
                k: null,
                loading: false,
                result: null,
            };
        },
        methods: {
            validateOnSubmit() {
                this.$validator.validateAll().then((success) => {
                    if (!success) {
                        return;
                    }

                    this.loading = true;
                    this.result = null;

                    const color = this.c + ',' + this.m + ',' + this.y + ',' + this.k;

                    axios.get('/api/v1/' + this.account + '/cmyktorgb', {
                        params: {
                            color,
                            icc: this.icc,
                        },
                    }).then((response) => {
                        this.loading = false;
                        this.statusType = null;
                        this.status = null;
                        this.result = response.data;
                    }).catch((error) => {
                        this.loading = false;
                        this.statusType = 'danger';
                        this.status = api.unpackError(error.response);
                    });
                });
            },
        },
    };
</script>

<style lang="less">
    .convert-container {
        max-width: 420px;

        margin-top: 20px;
        margin-left: auto;
        margin-right: auto;

        padding: 20px;

        border: 1px solid #d3dbe3;
        border-radius: 2px;
    }

    .cmyk-row {
        margin-left: -5px;
        margin-right: -5px;

        > div {
            padding-left: 5px;
            padding-right: 5px;
        }
    }

    .convert-result {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid #d3dbe3;
    }

    .result-swatches {
        margin-bottom: 10px;
    }

    .swatch {
        width: 100%;
        height: 60px;
        border: 1px solid #d3dbe3;
        border-radius: 2px;
    }
</style>
